// ============================================================
// THE MARGINS — THE PRESS RANK SYSTEM
// Retro editorial progression system
// ============================================================

export const TIERS = [
  {
    rank: 1,
    title: 'Cub',
    indicator: '[CUB]',
    ci: 0,
    badge: '✎',
    theme: 'default',
    color: 'var(--muted)',
    description: 'Welcome to the desk. Everyone starts here.',
  },
  {
    rank: 2,
    title: 'Stringer',
    indicator: '[STRINGER]',
    ci: 5,
    badge: '◌',
    theme: 'ink',
    color: 'var(--tier-stringer)',
    description: 'You filed your first dispatch. The editor noticed.',
  },
  {
    rank: 3,
    title: 'Correspondent',
    indicator: '[CORR]',
    ci: 15,
    badge: '◎',
    theme: 'ruled',
    color: 'var(--tier-correspondent)',
    description: 'A byline worth reading. Keep going.',
  },
  {
    rank: 4,
    title: 'Staff Writer',
    indicator: '[STAFF]',
    ci: 30,
    badge: '◉',
    theme: 'rust',
    color: 'var(--accent)',
    description: 'You have a desk. You have a voice. Use both.',
  },
  {
    rank: 5,
    title: 'Beat Reporter',
    indicator: '[BEAT]',
    ci: 55,
    badge: '◈',
    theme: 'beat',
    color: 'var(--tier-beat)',
    description: 'You own your territory. Readers know your name.',
  },
  {
    rank: 6,
    title: 'Feature Writer',
    indicator: '[FEATURE]',
    ci: 90,
    badge: '◐',
    theme: 'broadsheet',
    color: 'var(--tier-feature)',
    description: 'Long-form, considered, unhurried. The good stuff.',
  },
  {
    rank: 7,
    title: 'Columnist',
    indicator: '[COL]',
    ci: 140,
    badge: '↻',
    theme: 'pressroom',
    color: 'var(--tier-columnist)',
    description: 'You have a column. That means something.',
  },
  {
    rank: 8,
    title: 'Senior Correspondent',
    indicator: '[SR. CORR]',
    ci: 210,
    badge: '◆',
    theme: 'typewriter',
    color: 'var(--tier-senior-corr)',
    description: 'Seasoned. Trusted. The desk listens when you speak.',
  },
  {
    rank: 9,
    title: 'Contributing Editor',
    indicator: '[CONTRIB. ED]',
    ci: 300,
    badge: '◈',
    theme: 'aged',
    color: 'var(--tier-contrib-ed)',
    description: 'Your contributions shape what this place is.',
  },
  {
    rank: 10,
    title: 'Senior Editor',
    indicator: '[SR. ED]',
    ci: 420,
    badge: '◐',
    theme: 'wire',
    color: 'var(--tier-senior-ed)',
    description: 'A name that brings readers in before they read a word.',
  },
  {
    rank: 11,
    title: 'Editor at Large',
    indicator: '[ED. AT LARGE]',
    ci: 580,
    badge: '◆',
    theme: 'masthead',
    color: 'var(--tier-editor-large)',
    description: 'The masthead. You built this place with your words.',
  },
  {
    rank: 12,
    title: 'The Voice',
    indicator: '[VOICE]',
    ci: Infinity,
    badge: '◆',
    theme: 'voice',
    color: 'var(--tier-voice)',
    description: 'By appointment only. There is no higher honour.',
    legendary: true,
  },
]

// ── CI earning rules ──────────────────────────────────────────
export function calcCIForPost(post) {
  const words = post.body?.replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length || 0
  if (post.section === 'journal') return 35
  if (words >= 1200) return 22
  if (words >= 600) return 15
  return 8
}

export const CI_ACTIONS = {
  reaction_brought_here:    5,
  reaction_shifts_something: 4,
  reaction_sits_with_me:    3,
  reaction_other:           2,
  comment_received:         3,
  comment_replied_to:       2,
  first_publish_of_month:   5,
  streak_2_months:          10,
  streak_3_plus_months:     20,
  piece_of_month:           25,
}

// ── Rank lookup helpers ───────────────────────────────────────
export function getTierByCi(ci, isVoice = false) {
  if (isVoice) return TIERS[11]
  let tier = TIERS[0]
  for (const t of TIERS.slice(0, 11)) {
    if (ci >= t.ci) tier = t
    else break
  }
  return tier
}

export function getNextTier(currentRank) {
  if (currentRank >= 11) return null
  return TIERS[currentRank] // rank is 1-indexed, array is 0-indexed
}

export function getCiToNext(ci, currentRank) {
  const next = getNextTier(currentRank)
  if (!next) return null
  return next.ci - ci
}

export function getCiProgress(ci, currentRank) {
  const current = TIERS[currentRank - 1]
  const next = getNextTier(currentRank)
  if (!next) return 100
  const range = next.ci - current.ci
  const progress = ci - current.ci
  return Math.min(100, Math.round((progress / range) * 100))
}

// ── Theme CSS class map ───────────────────────────────────────
export const THEME_STYLES = {
  default: {
    card: {},
    border: 'var(--rule)',
    nameTreatment: {},
    animated: false,
  },
  ink: {
    card: { background: '#0F0E0C', color: '#EDE8DF' },
    border: '#2C2820',
    nameTreatment: { color: '#EDE8DF' },
    animated: false,
  },
  ruled: {
    card: {
      backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, rgba(200,64,26,0.08) 28px)',
      backgroundSize: '100% 28px',
    },
    border: '#7AB87A',
    nameTreatment: { color: '#7AB87A' },
    animated: false,
  },
  rust: {
    card: { background: 'linear-gradient(135deg, var(--paper) 0%, rgba(200,64,26,0.06) 100%)' },
    border: 'var(--accent)',
    nameTreatment: { color: 'var(--accent)' },
    animated: false,
  },
  beat: {
    card: { background: 'var(--paper)' },
    border: '#C87A3A',
    nameTreatment: { color: '#C87A3A' },
    animated: true,
    animation: 'press-pulse 3s ease-in-out infinite',
  },
  broadsheet: {
    card: {
      background: '#FAFAF7',
      borderTop: '3px solid #1A1A1A',
    },
    border: '#A87AC8',
    nameTreatment: {
      fontFamily: "'Space Grotesk', sans-serif",
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    animated: false,
  },
  pressroom: {
    card: {
      background: '#0F0E0C',
      color: '#EDE8DF',
      backgroundImage: 'radial-gradient(rgba(200,64,26,0.03) 1px, transparent 1px)',
      backgroundSize: '20px 20px',
    },
    border: '#C8A83A',
    nameTreatment: { color: '#C8A83A' },
    animated: false,
  },
  typewriter: {
    card: { background: 'var(--cream)' },
    border: '#C87A7A',
    nameTreatment: {
      fontFamily: "'Courier New', monospace",
      letterSpacing: '0.06em',
    },
    animated: true,
    animation: 'press-ribbon 4s linear infinite',
  },
  aged: {
    card: {
      background: '#F5EDD8',
      filter: 'sepia(0.15)',
    },
    border: '#B8A87A',
    nameTreatment: {
      color: '#6B4F1A',
      fontFamily: "'Space Grotesk', sans-serif",
      fontWeight: 700,
    },
    animated: false,
  },
  wire: {
    card: {
      background: '#0A0F14',
      color: 'var(--tier-senior-ed)',
      fontFamily: "'Courier New', monospace",
    },
    border: '#7AB8B8',
    nameTreatment: {
      color: 'var(--tier-senior-ed)',
      fontFamily: "'Courier New', monospace",
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
    animated: false,
  },
  masthead: {
    card: {
      background: '#0A0908',
      color: '#EDE8DF',
      borderTop: '3px solid #D4AF37',
    },
    border: '#D4AF37',
    nameTreatment: {
      color: 'var(--tier-editor-large)',
      fontFamily: "'Space Grotesk', sans-serif",
      fontWeight: 700,
      fontSize: '1.4rem',
    },
    animated: true,
    animation: 'press-glow 3s ease-in-out infinite',
  },
  voice: {
    card: {
      background: 'linear-gradient(135deg, #1A0804 0%, #0A0908 100%)',
      color: '#EDE8DF',
      borderTop: '3px solid #C8401A',
    },
    border: '#C8401A',
    nameTreatment: {
      color: 'var(--tier-voice)',
      fontFamily: "'Space Grotesk', sans-serif",
      fontWeight: 700,
      fontSize: '1.5rem',
    },
    animated: true,
    animation: 'press-crimson 2.5s ease-in-out infinite',
  },
}

// ── Rank-up messages ──────────────────────────────────────────
export const RANKUP_MESSAGES = {
  2:  { headline: 'STRINGER', sub: 'You filed your first dispatch. The editor noticed.' },
  3:  { headline: 'CORRESPONDENT', sub: 'A byline worth reading. Keep going.' },
  4:  { headline: 'STAFF WRITER', sub: 'You have a desk. You have a voice. Use both.' },
  5:  { headline: 'BEAT REPORTER', sub: 'You own your territory. Readers know your name.' },
  6:  { headline: 'FEATURE WRITER', sub: 'Long-form, considered, unhurried. The good stuff.' },
  7:  { headline: 'COLUMNIST', sub: 'You have a column. That means something.' },
  8:  { headline: 'SENIOR CORRESPONDENT', sub: 'Seasoned. Trusted. The desk listens when you speak.' },
  9:  { headline: 'CONTRIBUTING EDITOR', sub: 'Your contributions shape what this place is.' },
  10: { headline: 'SENIOR EDITOR', sub: 'A name that brings readers in before they read a word.' },
  11: { headline: 'EDITOR AT LARGE', sub: 'The masthead. You built this place with your words.' },
}
